import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { hasAdminAccess, getLocalhostAccessPreviewMode } from "@/lib/adminAccess";
import { isLocalDevAuthBypassEnabled } from "@/lib/localDevAuthBypass";
import { isClerkConfigured } from "@/lib/clerkEntitlements";
import { apiService } from "@/services/api";
import { getResolvedDashboardEmail } from "@/lib/dashboardIdentity";
import {
  getClientGates,
  normalizeClientGates,
  upsertClientGates,
  isAssessmentComplete,
  hasDashboardAccess,
} from "@/lib/clientGatesStore";

const verifiedGates = new Map();

function getVerifiedGates(email) {
  return verifiedGates.get(email)?.gates || null;
}

// Real route-level gate for client dashboard features (the sidebar lock is cosmetic only).
// gate="assessment_complete": requires the one-time eligibility assessment to be completed.
// gate="dashboard_access": requires an admin to have unlocked the full dashboard.
// Admins and the localhost bypass always pass.
function RequireClientGateInner({ gate, children }) {
  const location = useLocation();
  const email = getResolvedDashboardEmail() || "";
  // The localhost bypass only grants admin when no preview persona is set;
  // ?preview=customer must exercise the real client gating.
  const isAdmin = hasAdminAccess() || (isLocalDevAuthBypassEnabled() && !getLocalhostAccessPreviewMode());
  const [gates, setGates] = useState(() => {
    return (email && getVerifiedGates(email)) || (email ? getClientGates(email) : null);
  });
  const [resolved, setResolved] = useState(() => Boolean(email && getVerifiedGates(email)));

  useEffect(() => {
    if (isAdmin) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const payload = await apiService.getClientState();
        const record = payload?.record || null;
        if (record?.client_email) {
          upsertClientGates(record.client_email, record);
          const verified = normalizeClientGates(record);
          verifiedGates.set(record.client_email, { gates: verified });
          if (email) verifiedGates.set(email, { gates: verified });
        }
        if (!cancelled) setGates(record ? normalizeClientGates(record) : null);
      } catch {
        // Full dashboard access is backend-authoritative. If the backend is
        // unavailable, do not let a stale local unlock render protected pages.
        if (!cancelled && gate === "dashboard_access") setGates(null);
      } finally {
        if (!cancelled) setResolved(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, email, gate]);

  if (isAdmin) return children;

  // A run that just finished arrives with the results in router state before the
  // backend completion mark is guaranteed visible - let it through.
  const hasFreshResultState = Boolean(
    gate === "assessment_complete" &&
      (location.state?.assessmentData || location.state?.result || location.state?.jobId)
  );
  if (hasFreshResultState) return children;

  const allowed =
    gate === "assessment_complete" ? isAssessmentComplete(gates) : hasDashboardAccess(gates);

  // Not yet allowed by the local store: show a loader (not blank shell content) until the
  // backend record answers; a locally-allowed customer is not held up.
  if (!resolved) {
    if (gate === "dashboard_access") return <GateLoader />;
    return allowed ? children : <GateLoader />;
  }
  if (!allowed) {
    // The destination can explain what unlocks the page (components/auth/NoticeFromState.jsx).
    return (
      <Navigate
        to={gate === "assessment_complete" ? "/dashboard/eligibility-assessment" : "/dashboard"}
        replace
        state={{ gate, deniedRoute: location.pathname }}
      />
    );
  }
  return children;
}

function GateLoader() {
  return <p className="px-6 py-5 text-sm text-[#667085]" role="status">Checking workspace access...</p>;
}

function RequireClientGateClerk({ gate, children }) {
  const { isLoaded } = useAuth();
  if (!isLoaded) return <GateLoader />;
  return <RequireClientGateInner gate={gate}>{children}</RequireClientGateInner>;
}

export default function RequireClientGate({ gate, children }) {
  if (isLocalDevAuthBypassEnabled() && !getLocalhostAccessPreviewMode()) return children;
  if (!isClerkConfigured()) {
    return <RequireClientGateInner gate={gate}>{children}</RequireClientGateInner>;
  }
  return <RequireClientGateClerk gate={gate}>{children}</RequireClientGateClerk>;
}
